import qs from 'qs';
import md5 from 'md5';

/**
 * Check if ShowingTime is available for a given MLS
 * @param {string} mlsCode - Rails attr: current_user.mls_credential.try(:code)
 */

function isShowingTimeAvailable(mlsCode) {
  return mlsConfigs.hasOwnProperty(mlsCode);
}
/**
 * Check if ShowingTime is enabled for a given listing
 * @param {string} isEnabledResponse - Requested from isEnabledUrl, one of [ "f[0]=0;", "f[0]=1;" ]
 */

function isShowingTimeEnabled(isEnabledResponse) {
  return (isEnabledResponse || "").trim() === "f[0]=1;";
}
/**
 * Build the url used to query whether ShowingTime is enabled
 * @param {string} mlsCode - Rails attr: current_user.mls_credential.try(:code)
 * @param {string} mlsName - Rails attr: current_user.mls_credential.try(:name)
 * @param {string} mlsnum - Forge field: listing.mlsnum
 * @param {string} agentId - Forge field: listing.agent_list.mls_id
 * @param {string} agentOfficeId - Forge field: listing.office_list.id
 * @param {string} statusLabel - Forge field: listing.status
 */

function buildIsEnabledUrl(_ref) {
  var mlsCode = _ref.mlsCode,
      mlsName = _ref.mlsName,
      mlsnum = _ref.mlsnum,
      agentId = _ref.agentId,
      agentOfficeId = _ref.agentOfficeId,
      statusLabel = _ref.statusLabel;
  var _mlsConfigs$mlsCode = mlsConfigs[mlsCode],
      siteid = _mlsConfigs$mlsCode.siteid,
      mlsid = _mlsConfigs$mlsCode.mlsid,
      source = "S",
      offset = 0;
  var baseUrl = "https://icons.showingtime.com/sw/SW";
  var siteParams = "".concat(siteid, "~").concat(source, "~").concat(mlsName || agentId, "~").concat(mlsid, "~").concat(offset);
  var listingParams = "".concat(mlsnum, "~").concat(statusLabel, "~").concat(agentId, "~").concat(agentOfficeId || agentId);
  return "".concat(baseUrl, "?p=").concat(siteParams, ";").concat(listingParams);
}
/**
 * Build the url for the ShowingTime link
 * @param {string} mlsCode - Rails attr: current_user.mls_credential.try(:code)
 * @param {string} mlsName - Rails attr: current_user.mls_credential.try(:name)
 * @param {string} mlsnum - Forge field: listing.mlsnum
 * @param {string} requestingAgentId - Rails attr: current_user.mls_credential.name
 * @param {string} listingAgentId - Forge field: listing.agent_list.mls_id
 * @param {string} agentOfficeId - Forge field: listing.office_list.id
 * @param {string} statusLabel - Forge field: listing.status
 * @param {string} address - Forge field: listing.address
 * @param {string} city - Forge field: listing.city
 * @param {string} zip - Forge field: listing.zipcode
 * @param {string} price - Forge field: listing.price
 */

function buildShowingTimeUrl(_ref2) {
  var mlsCode = _ref2.mlsCode,
      mlsName = _ref2.mlsName,
      mlsnum = _ref2.mlsnum,
      requestingAgentId = _ref2.requestingAgentId,
      listingAgentId = _ref2.listingAgentId,
      agentOfficeId = _ref2.agentOfficeId,
      statusLabel = _ref2.statusLabel,
      address = _ref2.address,
      city = _ref2.city,
      zip = _ref2.zip,
      price = _ref2.price;
  var _mlsConfigs$mlsCode2 = mlsConfigs[mlsCode],
      siteid = _mlsConfigs$mlsCode2.siteid,
      mlsid = _mlsConfigs$mlsCode2.mlsid,
      salt = _mlsConfigs$mlsCode2.salt,
      state = _mlsConfigs$mlsCode2.state;
  var baseUrl = "https://schedulingsso.showingtime.com/external";
  var timeStamp = new Date().toISOString().split(".")[0] + "Z";
  var md5Params = "".concat(timeStamp).concat(mlsnum).concat(mlsName).concat(salt);
  var paramsObject = {
    siteid: siteid,
    mlsid: mlsid,
    listingid: mlsnum,
    raid: requestingAgentId,
    laid: listingAgentId,
    listingoffice: agentOfficeId || agentId,
    paddr: address,
    pcity: city,
    pstate: state,
    pzip: zip,
    price: price,
    lstatus: statusLabel,
    DTM: timeStamp,
    SIG: md5(md5Params).toUpperCase()
  };
  return "".concat(baseUrl, "?").concat(qs.stringify(paramsObject));
}
/**
 *
 * These are the only MLSs for which ShowingTime is available.
 * All of these values are provided by ShowingTime, except "state", which is hardcoded
 * because Forge does not normalize state values and they must be two characters to work.
 */

var mlsConfigs = {
  armls: {
    state: "AZ",
    siteid: "CLOUDMLX.ARMLS.I",
    mlsid: "ARMLS",
    salt: "1100513111-85281"
  },
  charleston: {
    state: "SC",
    siteid: "CLOUDMLX.CTAR.I",
    mlsid: "CTAR",
    salt: "1100513152-29418"
  },
  fmls: {
    state: "GA",
    siteid: "CLOUDMLX.FMLS.I",
    mlsid: "FMLS",
    salt: "1100513271-30342"
  },
  nnrmls: {
    state: "NV",
    siteid: "CLOUDMLX.NNRMLS.I",
    mlsid: "NNRMLS",
    salt: "905460-89502"
  },
  swflmls: {
    state: "FL",
    siteid: "CLOUDMLX.SWFL.I",
    mlsid: "SWFL",
    salt: "140925688-34109"
  },
  crmls_crib: {
    state: "CA",
    siteid: "CLOUDMLX.CRMLS-CA.I",
    mlsid: "CRMLS-CA",
    salt: "1100510363-91773"
  }
};

export { buildIsEnabledUrl, buildShowingTimeUrl, isShowingTimeAvailable, isShowingTimeEnabled };
